<template>
  <ReportLayoutModerno
    id="conferenceList"
    :prefecture="prefecture"
    :period="convertDate(filter.startDate) + ' até ' + convertDate(filter.endDate)"
  >
    <template v-slot:icon>mdi-file-document-outline</template>
    <template v-slot:title>RELATÓRIO INDIVIDUAL DE FATURAMENTO</template>
    <template v-slot:sub-header>
      <h3 class="text-sm font-bold text-slate-600">
        Fornecedor: <span class="text-orange-600 uppercase">{{ provider && provider.provider_name ? provider.provider_name : "" }}</span>
      </h3>
    </template>

    <!-- LISTAGEM POR SETOR -->
    <div v-for="(d, i) in data.supplies" :key="i" class="mb-10 break-inside-avoid shadow-sm border border-slate-200 rounded-2xl overflow-hidden">
      <!-- CABEÇALHO DO SETOR -->
      <div class="bg-slate-800 text-white px-8 py-4 flex items-center justify-between">
        <div class="flex flex-col">
          <span class="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">SETOR</span>
          <span class="text-sm font-bold uppercase tracking-wide">{{ d.sec_name }}</span>
        </div>
        <div class="flex items-center gap-6">
          <div class="text-right">
            <span class="text-[8px] font-black text-slate-400 uppercase block">SUBSETOR</span>
            <span class="text-[11px] font-bold uppercase">{{ d.subsec_desc || "---" }}</span>
          </div>
          <v-icon color="white/20" size="32">mdi-office-building-outline</v-icon>
        </div>
      </div>

      <!-- INFO DO VEÍCULO -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 p-6 bg-slate-50 border-b border-slate-200">
        <div class="flex flex-col">
          <span class="text-[9px] font-black text-slate-400 uppercase mb-1">VEÍCULO</span>
          <span class="text-sm font-black text-slate-800 uppercase">{{ d.brand_desc || "" }}</span>
        </div>
        <div class="flex flex-col">
          <span class="text-[9px] font-black text-slate-400 uppercase mb-1">MODELO</span>
          <span class="text-sm font-black text-slate-800 uppercase">{{ d.model_desc || "" }}</span>
        </div>
        <div class="flex flex-col">
          <span class="text-[9px] font-black text-slate-400 uppercase mb-1 tracking-widest">PLACA</span>
          <span class="text-sm font-black text-orange-600 bg-orange-50 px-2 py-0.5 rounded border border-orange-100 w-fit">{{ d.vehicle_board || "" }}</span>
        </div>
        <div class="flex flex-col">
          <span class="text-[9px] font-black text-slate-400 uppercase mb-1">COMBUSTÍVEL</span>
          <span class="text-sm font-bold text-slate-700 uppercase">{{ d.item_description_desc || "" }}</span>
        </div>
      </div>

      <!-- TABELA DE ABASTECIMENTOS / CUPONS -->
      <div class="p-6">
        <table class="w-full text-left order-collapse mb-6">
          <thead>
            <tr class="bg-slate-100/50">
              <th class="p-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">REGISTRO DE CUPONS / NF-e</th>
              <th class="p-4 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">LITROS</th>
              <th class="p-4 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">VALOR TOTAL</th>
            </tr>
          </thead>
          <tbody>
            <tr class="border-b border-slate-50">
              <td class="p-4">
                <div class="text-xs text-slate-600 font-medium leading-relaxed max-w-xl">
                  {{ d.vehiclesCoupons || "Nenhum cupom registrado" }}
                </div>
              </td>
              <td class="p-4 text-sm font-black text-slate-800 text-right whitespace-nowrap">
                {{ d.totalLiters ? realMaskII(d.totalLiters.toFixed(3)) : "0,000" }} Lts
              </td>
              <td class="p-4 text-sm font-black text-orange-600 text-right whitespace-nowrap font-mono">
                R$ {{ d.totalValue ? realMask(d.totalValue.toFixed(2)) : "0,00" }}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- RESUMO DO SETOR -->
        <div class="flex justify-end">
          <div class="bg-slate-900 text-white rounded-xl px-10 py-5 flex items-center gap-12 shadow-md">
            <div class="flex flex-col">
              <span class="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">TOTAL LITROS</span>
              <span class="text-lg font-black tracking-tight">{{ d.totalLiters ? realMaskII(Number(d.totalLiters).toFixed(3)) : 0 }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-[8px] font-black text-orange-400 uppercase tracking-widest mb-1">TOTAL VALOR</span>
              <span class="text-lg font-black tracking-tight text-orange-400 font-mono">R$ {{ d.totalValue ? realMask(Number(d.totalValue).toFixed(2)) : 0 }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- RESUMO GERAL DO RELATÓRIO -->
    <div class="mt-16 break-inside-avoid">
       <div class="bg-indigo-900 rounded-3xl p-10 text-white shadow-2xl relative overflow-hidden">
          <v-icon color="white/5" size="180" class="absolute -right-10 -bottom-10 pointer-events-none">mdi-file-find</v-icon>
          
          <h2 class="text-xl font-black uppercase tracking-[0.3em] mb-10 border-b border-white/10 pb-4">RESUMO GERAL POR ITEM</h2>
          
          <table class="w-full text-left">
            <thead>
              <tr class="text-[10px] font-black uppercase tracking-widest text-indigo-300">
                <th class="py-4 px-2">ITEM DESCRIÇÃO</th>
                <th class="py-4 px-2 text-right">LITROS TOTAIS</th>
                <th class="py-4 px-2 text-right">VALOR TOTAL ACUMULADO</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(res, t) in data.resume" :key="t" class="border-t border-white/10">
                <td class="py-5 px-2 font-bold text-sm uppercase">{{ res.item_description_desc }}</td>
                <td class="py-5 px-2 text-right font-black text-base">{{ res.totalLiters ? realMaskII(res.totalLiters.toFixed(3)) : 0 }}</td>
                <td class="py-5 px-2 text-right font-black text-base text-orange-400 font-mono">R$ {{ res.totalValue ? realMask(res.totalValue.toFixed(2)) : 0 }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="border-t-4 border-orange-500/50 bg-white/5">
                <td class="py-8 px-4 font-black uppercase tracking-widest text-lg">TOTAL FINAL</td>
                <td class="py-8 px-2 text-right"></td> <!-- Espaço reservado se houvesse soma de litros diferentes -->
                <td class="py-8 px-4 text-right">
                  <div class="flex flex-col items-end">
                    <span class="text-[10px] font-black text-orange-400 uppercase tracking-widest mb-1">VALOR TOTAL DO PERÍODO</span>
                    <span class="text-3xl font-black text-white font-mono tracking-tighter">R$ {{ data.supplies[0] && data.supplies[0].totalValue ? realMask(data.supplies[0].totalValue.toFixed(2)) : 0 }}</span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
       </div>
    </div>
  </ReportLayoutModerno>
</template>

<script>
import helpers from "@/mixins/helpers";
import ReportLayoutModerno from "@/components/ReportLayoutModerno.vue";

export default {
  components: { ReportLayoutModerno },
  props: ["data", "filter", "prefecture", "provider"],
  mixins: [helpers],
  methods: {
    initialize() {
      this.helpers;
    },
  },
};
</script>

<style scoped>
@media print {
  .v-container {
    padding: 0 !important;
    max-width: none !important;
  }
  .bg-slate-50 {
    background-color: #f8fafc !important;
    -webkit-print-color-adjust: exact;
  }
  .bg-slate-100 {
    background-color: #f1f5f9 !important;
    -webkit-print-color-adjust: exact;
  }
  .bg-slate-800 {
    background-color: #1e293b !important;
    -webkit-print-color-adjust: exact;
  }
  .bg-slate-900 {
    background-color: #0f172a !important;
    -webkit-print-color-adjust: exact;
  }
  .bg-indigo-900 {
    background-color: #312e81 !important;
    -webkit-print-color-adjust: exact;
  }
  .bg-orange-50 {
    background-color: #fff7ed !important;
    -webkit-print-color-adjust: exact;
  }
  .text-white {
    color: white !important;
  }
  .text-orange-600 {
    color: #ea580c !important;
  }
  .text-orange-400 {
    color: #fb923c !important;
  }
}

.shadow-2xl {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}
</style>